<script lang="ts">
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import type { MapOverviewStoryblok } from "$types/components";
  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor } from "$types/atoms";
  import { storyblokEditable } from "@storyblok/svelte";
  import StatsList from "./StatsList.svelte";
  import TextCard from "$lib/elements/TextCard.svelte";
  import MapOverviewCareerLocation from "$lib/molecules/careers/MapOverviewCareerLocation.svelte";
  import { onMount } from "svelte";
  import CareerDepartmentsSlider from "$lib/molecules/careers/CareerDepartmentsSlider.svelte";
  import Marquee from "svelte-fast-marquee";

  export let blok: MapOverviewStoryblok;

  // Reference to calculate left and top of each location
  const positionBaseRef = [1200, 653];
  let mapView;
  let isCareerLocations = blok?.careersLocations === true;

  function getTopPosition(pos: string) {
    return (parseInt(pos) * 100) / positionBaseRef[1];
  }

  function getLeftPosition(pos: string) {
    return (parseInt(pos) * 100) / positionBaseRef[0];
  }

  $: hasStats = () => {
    if (blok?.statsList) {
      if (blok.statsList.length > 0) {
        return true;
      }
      return false;
    }
  };

  $: hasBottomStats = () => hasStats() && blok.statsPosition === "bottom";
  $: hasLeftStats = () => hasStats() && blok.statsPosition !== "bottom";

  $: jobs = [];

  onMount(async () => {
    if (isCareerLocations) {
      const res = await fetch("/api/careers");
      const data = await res.json();
      jobs = data;
    }
  });

  let mobileCountries = [
    { name: "Côte d’Ivoire", icon: "/images/flags/cote.svg" },
    { name: "Ghana", icon: "/images/flags/ghana.webp" },
    { name: "Japan", icon: "/images/flags/japan.webp" },
    { name: "Kenya", icon: "/images/flags/kenya.webp" },
    { name: "Nigeria", icon: "/images/flags/nigeria.webp" },
    { name: "Rwanda ", icon: "/images/flags/rwanda.webp" },
    { name: "United States ", icon: "/images/flags/usa.svg" },
  ];
</script>

{#if blok.countries}
  <div class="max-w-[100vw] w-full overflow-hidden">
    <section
      class="section map-overview relative !overflow-visible {isCareerLocations
        ? '!pt-2 md:!pt-20 lg:!pt-28 !pb-20'
        : '!pt-6 md:!pt-[48px] lg:!pt-[56px] xl:!pt-[64px]'}"
      use:storyblokEditable={blok}
    >
      {#if isCareerLocations}
        <GridContainer>
          <GridItem col={12}>
            <div class="flex flex-col md:flex-row justify-between mb-2">
              <div class="max-w-[611px]">
                <h2 class="type:h2 mb-4">Let's work together.</h2>
                <p class="type:body-medium">
                  We have all kinds of roles open across the world. Take a look
                  at careers in your area and see if there's an opportunity that
                  fits your skill set.
                </p>
              </div>

              <div class="mt-4 md:mt-0 ml-0 md:ml-12">
                <Button href="/careers/open-roles" color={ButtonColor.BLACK}
                  ><div class="text-[12px]">See all open roles</div></Button
                >
              </div>
            </div>
          </GridItem>
        </GridContainer>
      {/if}
      {#if blok.headline}
        <GridContainer alignItems="center" class="overflow-visible">
          <GridItem col={12}>
            <h3
              class="type:h2 grid-container lg:-mb-[1.5em] relative z-10 text-pretty"
            >
              {blok.headline}
            </h3>
          </GridItem>
        </GridContainer>
      {/if}
      {#if blok.textCard && blok.textCard?.length > 0}
        <GridContainer alignItems="center" class="overflow-visible">
          <GridItem col={12}>
            <div class="section-bottom">
              <TextCard blok={blok.textCard[0]} />
            </div>
          </GridItem>
        </GridContainer>
      {/if}
      {#if blok.showLegend}
        <ul
          class="legend hidden absolute right-[40px] pt-[36px] lg:right-[8%] lg:pt-[100px] justify-right z-20 md:flex flex-row gap-[2px] type:h5"
        >
          <li
            class="md:p-[12px] xl:p-[18px] bg-sand"
            style="color: rgb(var(--color-red-secondary))"
          >
            Platform 1
          </li>
          <li
            class="md:p-[12px] xl:p-[18px] bg-sand"
            style="color: rgb(var(--color-blue-primary))"
          >
            Platform 2
          </li>
        </ul>
      {/if}
      <GridContainer
        class={blok.showLegend ? "no-padding" : ""}
        direction={isCareerLocations ? "column" : "column-reverse"}
        directionMd="row"
        alignItems="center"
        bgColor={hasLeftStats() ? "bg-sand !py-10 lg:!py-0" : ""}
      >
        {#if hasLeftStats()}
          <GridItem col={12} colMd={3}>
            <StatsList blok={blok.statsList?.[0]} />
          </GridItem>
        {/if}

        <GridItem
          col={12}
          colMd={hasLeftStats() ? 9 : 12}
          class={isCareerLocations ? "hidden md:block" : ""}
        >
          <div class={`map-wrapper ${hasLeftStats() ? "pb-10" : ""}`}>
            <div class="map-inner" bind:this={mapView}>
              <img
                class="map"
                src="/svg/overview-map.svg"
                alt="Map overview topography"
                loading="lazy"
              />
              <img
                class="map-grid"
                src="/images/map-grid-overlay.webp"
                alt="Map overview grid"
                loading="lazy"
              />

              <div class="location-wrapper">
                {#each blok.countries as item}
                  <div
                    class="location"
                    class:bottom-position={parseInt(item.direction, 10) === -1}
                    style={`left: ${getLeftPosition(
                      item.leftPosition ?? "0",
                    )}%; top: ${getTopPosition(
                      item.topPosition ?? "0",
                    )}%; --marker-color: ${
                      item.color || "var(--color-red-secondary)"
                    }`}
                  >
                    {#if item.component === "mapOverviewCareerLocation"}
                      <MapOverviewCareerLocation {jobs} blok={item} />
                    {:else}
                      <div class="marker" />
                      <div class="data">
                        <span class={`line ${item.lineSize}`} />
                        <div
                          class="location-name"
                          class:label-left={item.labelLeft ?? false}
                        >
                          <p class="type:label-fat">{item.name}</p>
                          <p class="type:label">{item.date}</p>
                        </div>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          </div>
        </GridItem>
        {#if hasBottomStats()}
          <GridItem col={12} class="md:-mt-[50px]">
            <StatsList blok={blok.statsList?.[0]} dividers="subtle" />
          </GridItem>
        {/if}
        {#if isCareerLocations}
          <GridItem col={12} class="mt-8 md:mt-0">
            <CareerDepartmentsSlider
              customOrder={blok?.careersDepartmentsCustomOrder
                ? blok?.careersDepartmentsCustomOrder?.map((b) => b.name)
                : []}
              {jobs}
            />
          </GridItem>
        {/if}
      </GridContainer>
    </section>

    <div class="md:hidden !pb-2">
      <div class="mb-4 section-header-width mx-auto">
        <div class="sm:px-[20px] max-w-[1248px] mx-auto">
          {#if blok.showLegend}
            <ul
              class="mobile-legend z-20 flex flex-row gap-[8px] type:h5"
            >
              <li
                class="py-[5px] px-[6px] bg-white"
                style="color: rgb(var(--color-red-secondary))"
              >
                Platform 1
              </li>
              <li
                class="py-[5px] px-[6px] bg-white"
                style="color: rgb(var(--color-blue-primary))"
              >
                Platform 2
              </li>
            </ul>
          {/if}
        </div>
      </div>
      <div class="py-4 bg-[#EBE8E1] mb-2">
        <Marquee speed={10} pauseOnHover={false}>
          {#each mobileCountries.concat(mobileCountries) as c}
            <div class="flex items-center space-x-2 px-[13px]">
              <div
                class="w-[20px] h-[13px] rounded-[2px] {c.icon
                  ? ''
                  : 'bg-green'} overflow-hidden"
              >
                {#if c.icon}
                  <img
                    class="w-full h-full object-cover"
                    src={c.icon}
                    alt={c.name}
                    loading="lazy"
                  />
                {/if}
              </div>
              <div class="text-black relative top-[1px]">{c.name}</div>
            </div>
          {/each}
        </Marquee>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .section-bottom {
    padding-bottom: fluid(60px, 80px);
    @media screen and (max-width: 768px) {
      padding-bottom: 40px;
    }
  }

  .map-overview {
    overflow: hidden;
  }

  .map-wrapper {
    position: relative;
    width: 100%;
  }

  .map-inner {
    width: 100%;
    padding: fluid(44px) fluid(36px);
    position: relative;

    @include breakpoint("small") {
      width: 100%;
    }
  }

  .map {
    width: 100%;
    position: relative;
    aspect-ratio: auto 726 / 395;
  }

  .map-grid {
    position: absolute;
    z-index: 7;
    top: 0;
    left: 0;
    width: 100%;
  }

  .location-wrapper {
    position: absolute;
    z-index: 8;
    height: 100%;
    width: 100%;
    //background: #000;
    opacity: 1;
    top: 0;
    left: 0;
  }

  .location {
    position: absolute;

    .marker {
      height: 4px;
      width: 4px;

      @include breakpoint("small") {
        height: fluid(10px, 10px);
        width: fluid(10px, 10px);
        border-radius: 1px;
      }
      background-color: rgb(var(--color-light-grey));
      border-color: rgb(var(--marker-color));
      border-width: 1.5px;
      border-style: solid;
      position: relative;
    }

    .data {
      position: absolute;
      left: fluid(5px, 5px);
      top: fluid(-6px, -6px);
      transform: translateY(-100%);
      opacity: 0;

      @include breakpoint("small") {
        opacity: 1;
      }
    }

    .line {
      width: 1px;
      height: fluid(80px, 80px);
      background: rgb(var(--marker-color));
      display: block;

      &.nano {
        height: fluid(24px, 24px);
      }

      &.tiny {
        height: fluid(64px, 64px);
      }

      &.medium {
        height: fluid(102px, 102px);
      }

      &.large {
        height: fluid(140px, 140px);
      }
    }

    .location-name {
      position: absolute;
      left: 9px;
      top: 0;

      p {
        color: rgb(var(--marker-color));
        white-space: nowrap;
      }

      p:nth-child(2) {
        display: none;

        @include breakpoint("small") {
          display: block;
        }
      }

      &.label-left {
        left: -6px;
        text-align: right;
        transform: translateX(-100%);
      }
    }

    &.bottom-position {
      .data {
        top: auto;
        bottom: -6px;
        transform: translateY(100%);
      }

      .location-name {
        top: auto;
        bottom: -0.2em;
      }
    }
  }

  .mobile-legend li {
    border-radius: var(--border-radius-small);
  }

  .legend {
    li {
      border-radius: var(--border-radius-wrapper);
    }
    li:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    li:last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
</style>
